import { FACE_DETECTION_CONFIG } from "../constants";

// Helper function to load image from source
const loadImageFromSource = async (source) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) =>
      reject(
        new Error("Failed to load image: possibly corrupted or invalid format")
      );
    if (typeof source === "string") {
      img.crossOrigin = "anonymous"; // Important - add CORS support
      img.src = source;
    } else if (source instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = (err) => reject(new Error("Failed to read image file"));
      reader.readAsDataURL(source);
    } else {
      reject(
        new Error("Invalid source type: must be dataURL string or File object")
      );
    }
  });
};

/**
 * Validates a face image using the same criteria as the camera component
 *
 * This function performs comprehensive face validation including:
 * - Model loading with timeout protection
 * - Face detection using TinyFaceDetector
 * - Single face verification (rejects multiple or no faces)
 * - Face positioning validation (must be centered)
 * - Face size validation (not too close or too far)
 * - Edge proximity checking (faces can't be too close to image borders)
 *
 * @param {string|File} source - Image source (data URL string or File object)
 * @param {Object} distanceConfig - Optional config override for face validation thresholds
 * @returns {Promise<Object>} Validation result with valid boolean, message, and optional error code
 */
const validateFace = async (source, distanceConfig = {}) => {
  // Merge default configuration with any overrides provided
  const cfg = {
    minRatio: FACE_DETECTION_CONFIG.ratio.min,
    maxRatio: FACE_DETECTION_CONFIG.ratio.max,
    messages: FACE_DETECTION_CONFIG.messages,
  };

  try {
    // Verify face-api.js library is available
    if (!window.faceapi) {
      console.error("❌ face-api לא זמין!");
      return {
        valid: false,
        message: "שגיאה: ספריית זיהוי פנים לא נטענה",
        error: "face_api_missing",
      };
    }

    // Load the image with timeout to prevent long-running operations
    let img;
    try {
      const imageLoadPromise = loadImageFromSource(source);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Image loading timeout")), 7000)
      );

      img = await Promise.race([imageLoadPromise, timeoutPromise]);
    } catch (err) {
      console.error("❌ שגיאה בטעינת התמונה:", err);
      return {
        valid: false,
        message: `שגיאה בטעינת התמונה: ${err.message}`,
        error: "image_load_error",
      };
    }

    // Check image dimensions - prevent processing extremely large images
    // Prevent processing of extremely large images that could cause performance issues
    if (img.width > 8000 || img.height > 8000) {
      return {
        valid: false,
        message: "התמונה גדולה מדי - יש להשתמש בתמונה קטנה יותר",
        error: "image_too_large",
      };
    }

    // Load required ML models for face detection with timeout protection
    const MODEL_URL = `${process.env.PUBLIC_URL || ""}/models`;
    try {
      if (
        !window.faceapi.nets.tinyFaceDetector.isLoaded ||
        !window.faceapi.nets.faceLandmark68Net.isLoaded
      ) {
        // Load models in parallel with timeout to prevent hanging
        const modelLoadPromise = Promise.all([
          !window.faceapi.nets.tinyFaceDetector.isLoaded
            ? window.faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL)
            : Promise.resolve(),
          !window.faceapi.nets.faceLandmark68Net.isLoaded
            ? window.faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL)
            : Promise.resolve(),
        ]);

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Model loading timeout")), 15000)
        );

        await Promise.race([modelLoadPromise, timeoutPromise]);
      }
    } catch (err) {
      console.error("❌ שגיאה בטעינת מודלי זיהוי פנים:", err);
      return {
        valid: false,
        message: `שגיאה בטעינת מודל זיהוי פנים: ${err.message}`,
        error: "model_load_error",
      };
    }

    // Perform face detection with landmarks using TinyFaceDetector model
    let detections;
    try {
      const detectionPromise = window.faceapi
        .detectAllFaces(
          img,
          new window.faceapi.TinyFaceDetectorOptions({
            inputSize: FACE_DETECTION_CONFIG.model.inputSize, // Balance between speed and accuracy
            scoreThreshold: FACE_DETECTION_CONFIG.model.confidenceThreshold, // Minimum confidence for detection
          })
        )
        .withFaceLandmarks(); // Include facial landmarks for more precise validation

      // Add timeout protection to prevent hanging on problematic images
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Face detection timeout")), 10000)
      );

      detections = await Promise.race([detectionPromise, timeoutPromise]);
    } catch (err) {
      console.error("❌ שגיאה בזיהוי פנים:", err);
      return {
        valid: false,
        message: "שגיאה בזיהוי פנים, נא לנסות תמונה אחרת",
        error: "detection_error",
      };
    }

    // No faces detected
    if (!detections || detections.length === 0) {
      return {
        valid: false,
        message: cfg.messages.noFace,
        error: "no_face",
      };
    }

    // Multiple faces detected
    // Validate detection results: we need exactly one face
    if (detections.length > 1) {
      return {
        valid: false,
        message: cfg.messages.multipleFaces,
        error: "multiple_faces",
      };
    }

    // Scale detection results to match original image dimensions
    const face = window.faceapi.resizeResults(detections[0], {
      width: img.width,
      height: img.height,
    });
    const { box } = face.detection;

    // Calculate face center coordinates for positioning validation
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;
    const centerThreshold = FACE_DETECTION_CONFIG.position.centerThreshold;
    const edgeBuffer = FACE_DETECTION_CONFIG.position.edgeBuffer;

    const centered =
      Math.abs(cx - img.width / 2) < img.width * centerThreshold &&
      Math.abs(cy - img.height / 2) < img.height * centerThreshold;

    // New check - are the faces far enough from image edges
    const isFullyInFrame =
      box.x > edgeBuffer &&
      box.y > edgeBuffer &&
      box.x + box.width < img.width - edgeBuffer &&
      box.y + box.height < img.height - edgeBuffer;

    if (!centered) {
      return {
        valid: false,
        message: cfg.messages.notCentered,
        error: "face_not_centered",
      };
    }

    // New check - are the faces far enough from the edges
    if (!isFullyInFrame) {
      return {
        valid: false,
        message: cfg.messages.notCentered,
        error: "face_too_close_to_edge",
      };
    }

    // Check face size ratio
    const ratio = box.height / img.height;

    if (ratio < cfg.minRatio) {
      return {
        valid: false,
        message: cfg.messages.tooFar,
        error: "face_too_small",
      };
    }

    if (ratio > cfg.maxRatio) {
      return {
        valid: false,
        message: cfg.messages.tooNear,
        error: "face_too_large",
      };
    }

    // All checks passed
    return {
      valid: true,
      message: cfg.messages.good,
      faceData: {
        box,
        landmarks: face.landmarks.positions,
        ratio,
      },
    };
  } catch (err) {
    console.error("❌ שגיאה כללית באימות פנים:", err);
    return {
      valid: false,
      message: `שגיאה בבדיקת התמונה: ${err.message || "נא לנסות שנית"}`,
      error: "unknown_error",
    };
  }
};

export default validateFace;
export { loadImageFromSource }; // Export for use in createPreview.js
